import { motion } from "framer-motion";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Arrow } from "../svg/worldSvgs/rmc/Achievements";
import { timeReform } from "../../utils";
export const Trivia = ({
  loading,
  loadingLength,
  score,
  timeLeft,
  currentQuestion,
  questions,
  currentQ,
  handleAnswerSelect,
  selectedAnswer,
  selectedAnswers,
  handleBack,
  handleSubmitWithPause,
  isLastQuestion,
  handleNext,
  isArena,
  color,
}) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 3, delay: 0.4 }}
      className={clsx(
        isArena
          ? "relative pt-2 pb-10 px-5 flex flex-col"
          : "relative pt-10 pb-10 px-5 h-svh flex flex-col",
      )}
    >
      {/* Header */}
      {!isArena && (
        <motion.div className="relative w-full flex items-center justify-center mb-8 flex-shrink-0">
          <FaLongArrowAltLeft
            onClick={() => navigate(-1)}
            className="text-lg text-white absolute left-0 cursor-pointer"
          />
          <p className="text-center text-xl text-white font-semibold">
            Trivia Challenge
          </p>
        </motion.div>
      )}

      {loading ? (
        <div className="flex flex-col h-full">
          {/* Fixed skeleton elements */}
          <div className="flex-shrink-0">
            <div className="h-16 bg-gray-600 rounded animate-pulse mt-5 mb-4"></div>
            <div className="space-y-2 mb-12">
              <div className="h-3 bg-gray-600 rounded animate-pulse"></div>
              <div className="h-3 bg-gray-600 rounded animate-pulse"></div>
            </div>
          </div>

          {/* Scrollable skeleton options */}
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 px-2 hidden-scrollbar">
            {loadingLength.map((_, index) => (
              <div
                key={index}
                className="h-10 bg-gray-600 rounded animate-pulse"
              ></div>
            ))}
          </div>

          {/* Fixed bottom buttons */}
          <div className="flex justify-between items-center flex-shrink-0 py-7">
            <div className="h-9 bg-gray-600 rounded animate-pulse w-1/4"></div>
            <div className="h-9 bg-gray-600 rounded animate-pulse w-1/4"></div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Score and Time */}
          <div className="flex justify-between items-center mb-6 flex-shrink-0">
            <div className="text-white">
              <span className="text-sm opacity-80">Score</span>
              <div className="text-xl font-bold">{score}</div>
            </div>
            <div className="text-white text-right">
              <span className="text-sm opacity-80">Time</span>
              <div className="text-xl font-bold">{timeReform(timeLeft)}</div>
            </div>
          </div>

          {/* Question Number and Progress */}
          <div className="mb-6 flex-shrink-0">
            <h2 className="text-white text-xl font-semibold mb-4">
              Question {currentQuestion + 1} ({currentQ?.points} pts)
            </h2>
            <div className="flex gap-1 mb-6">
              {questions?.map((_, index) => {
                return (
                  <div
                    key={index}
                    className={clsx(
                      "h-1 flex-1 rounded",
                      index > currentQuestion && "bg-gray-600",
                      !color &&
                        index <= currentQuestion &&
                        (isArena ? "bg-[#094eff]" : "bg-[#00FF88]"),
                    )}
                    style={
                      color && index <= currentQuestion
                        ? { backgroundColor: color }
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>

          {/* Question */}
          <div className="mb-6 flex-shrink-0">
            <p className="text-white text-lg leading-relaxed">
              {currentQ?.question}
            </p>
          </div>

          {/* Main Content Area - Scrollable Options and Fixed Buttons */}
          <div className="flex flex-col flex-1 min-h-0">
            {/* Answer Options - Scrollable */}
            <div className="space-y-4 overflow-y-auto hidden-scrollbar flex-1 min-h-0 mb-5 pb-3">
              {currentQ?.choices.map((option, index) => {
                return (
                  <div
                    key={index}
                    onClick={() => handleAnswerSelect(option)}
                    className={clsx(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all",
                      !color &&
                        (isArena
                          ? "border-[#2741d6] bg-[#2741d6]/10"
                          : "border-[#FFD700] bg-[#FFD700]/10"),
                    )}
                    style={
                      color
                        ? {
                            borderColor: color,
                            backgroundColor: `${color}1A`,
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm max-w-[290px]">
                        {option?.choice}
                      </span>
                      {selectedAnswer?.answer_id === option?.id && (
                        <div className="w-6 h-6 bg-[#00FF88] rounded-full flex items-center justify-center">
                          <span className=" text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons - Fixed at Bottom */}
            <div className="flex justify-between items-center flex-shrink-0">
              <button
                onClick={handleBack}
                disabled={currentQuestion === 0}
                className={clsx(
                  "py-3 px-5 flex items-center gap-2 rounded-lg font-medium text-base transition-colors",
                  !color &&
                    (isArena
                      ? {
                          "bg-[#094eff] text-white hover:bg-[#094eff]/90":
                            currentQuestion > 0,
                          "bg-[#094eff]/50 text-white/50 cursor-not-allowed":
                            currentQuestion === 0,
                        }
                      : {
                          "bg-[#FFD700] text-black hover:bg-[#FFD700]/90":
                            currentQuestion > 0,
                          "bg-[#FFD700]/50 text-black/50 cursor-not-allowed":
                            currentQuestion === 0,
                        }),
                  color && {
                    "text-white": currentQuestion > 0,
                    "text-white/50 cursor-not-allowed": currentQuestion === 0,
                  },
                )}
                style={
                  color
                    ? {
                        backgroundColor:
                          currentQuestion > 0 ? color : `${color}80`,
                      }
                    : undefined
                }
              >
                <Arrow
                  className="rotate-90"
                  stroke={color ? "white" : isArena ? "white" : "black"}
                />
                Back
              </button>

              {isLastQuestion ? (
                <button
                  onClick={handleSubmitWithPause}
                  disabled={
                    selectedAnswers[currentQuestion] === null ||
                    selectedAnswers[currentQuestion] === undefined
                  }
                  className={clsx(
                    "px-5 py-3 flex items-center gap-2 rounded-lg font-medium text-base transition-colors",
                    !color &&
                      (isArena
                        ? selectedAnswers[currentQuestion] !== null &&
                          selectedAnswers[currentQuestion] !== undefined
                          ? "bg-[#00FF88] text-black hover:bg-[#00FF88]/90"
                          : "bg-[#2741d6]/50 text-black/50 cursor-not-allowed"
                        : selectedAnswers[currentQuestion] !== null &&
                            selectedAnswers[currentQuestion] !== undefined
                          ? "bg-[#00FF88] text-black hover:bg-[#00FF88]/90"
                          : "bg-[#FFD700]/50 text-black/50 cursor-not-allowed"),
                    color && {
                      "text-white":
                        selectedAnswers[currentQuestion] !== null &&
                        selectedAnswers[currentQuestion] !== undefined,
                      "text-white/50 cursor-not-allowed":
                        selectedAnswers[currentQuestion] === null ||
                        selectedAnswers[currentQuestion] === undefined,
                    },
                  )}
                  style={
                    color
                      ? {
                          backgroundColor:
                            selectedAnswers[currentQuestion] !== null &&
                            selectedAnswers[currentQuestion] !== undefined
                              ? color
                              : `${color}80`,
                        }
                      : undefined
                  }
                >
                  <Tick /> Submit
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={
                    selectedAnswers[currentQuestion] === null ||
                    selectedAnswers[currentQuestion] === undefined
                  }
                  className={clsx(
                    "px-5 py-3 flex items-center gap-2 rounded-lg font-medium text-base transition-colors",
                    !color &&
                      (isArena
                        ? selectedAnswers[currentQuestion] !== null &&
                          selectedAnswers[currentQuestion] !== undefined
                          ? "bg-[#2741d6] text-white hover:bg-[#2741d6]/90"
                          : "bg-[#2741d6]/50 text-white/50 cursor-not-allowed"
                        : selectedAnswers[currentQuestion] !== null &&
                            selectedAnswers[currentQuestion] !== undefined
                          ? "bg-[#FFD700] text-black hover:bg-[#FFD700]/90"
                          : "bg-[#FFD700]/50 text-black/50 cursor-not-allowed"),
                    color && {
                      "text-white":
                        selectedAnswers[currentQuestion] !== null &&
                        selectedAnswers[currentQuestion] !== undefined,
                      "text-white/50 cursor-not-allowed":
                        selectedAnswers[currentQuestion] === null ||
                        selectedAnswers[currentQuestion] === undefined,
                    },
                  )}
                  style={
                    color
                      ? {
                          backgroundColor:
                            selectedAnswers[currentQuestion] !== null &&
                            selectedAnswers[currentQuestion] !== undefined
                              ? color
                              : `${color}80`,
                        }
                      : undefined
                  }
                >
                  <Arrow
                    className="-rotate-90"
                    stroke={color ? "white" : isArena ? "white" : "black"}
                  />{" "}
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const Tick = () => {
  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.5 6.99984L9.5 18.9998L4 13.4998L5.41 12.0898L9.5 16.1698L20.09 5.58984L21.5 6.99984Z"
        fill="black"
      />
    </svg>
  );
};
